import React, { useState, useEffect, useReducer, Fragment, useRef, useCallback, useMemo, memo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import axiosInstance from "../../../../api/axiosInstance";
import AvailabilityControl from "./AvailabilityControl";
import { useHeaderSlot } from "../../../../contexts/HeaderSlotContext";
import {
  LuChevronRight as ChevronRight,
  LuMenu as Menu,
  LuBell as Bell,
  LuUser as User,
  LuLogOut as LogOut,
  LuChevronDown as ChevronDown,
  LuInbox as Inbox,
} from "react-icons/lu";
import { getWsBaseUrl, getWsUrl } from "../../../../utils/wsUrl";
import { resolveAssetUrl } from "../../../../utils/resolveAssetUrl";

/* ROUTE LABEL MAP */
const ROUTE_MAP = {
  "/overview": "Overview",
  "/tickets": "Tickets",
  "/inbox": "Inbox",
  "/queues": "Queues",
  "/knowledge-base": "Knowledge Base",
  "/forms": "Forms",
  "/my-profile": "My Profile",
};

const MAIN_PAGE_PATHS = [
  "",
  "/",
  "/overview",
  "/tickets",
  "/inbox",
  "/queues",
  "/knowledge-base",
  "/forms",
  "/my-profile",
];

const toText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return value.content ?? value.message ?? value.name ?? value.title ?? value.token ?? JSON.stringify(value);
  }
  return String(value);
};

const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  Helper: get ticket name from various sources (pure function)            */
/* ────────────────────────────────────────────────────────────────────────── */
function getTicketName(ticketId, location, searchParams) {
  let fromQuery = searchParams.get("ticket_name");
  if (fromQuery) {
    try {
      fromQuery = decodeURIComponent(fromQuery);
    } catch (e) { }
    return fromQuery;
  }

  if (location.state?.title) return location.state.title;

  const stored = safeStorageGet(`ticket_title_${ticketId}`);
  if (stored) return stored;

  return null;
}

/* ─── INBOX BUTTON (glassmorphism pill) ────────────────── */
const InboxButton = memo(({ onClick, active = false }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all cursor-pointer
      ${active
        ? "border border-[#3250FF] text-[#3250FF]"
        : "border border-[#EBE7F8] text-[#161329] hover:border-[#3250FF] hover:text-[#3250FF]"
      }`}
    style={{
      background: "#FFFFFFB2",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
    }}
  >
    <Inbox className="w-4 h-4" />
    <span>Inbox</span>
  </button>
));

InboxButton.displayName = "InboxButton";

/* ─── MODERN NOTIFICATION BELL ───────────────────────────────────────────── */
const NotificationBell = memo(() => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [agentId, setAgentId] = useState(null);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const fetchId = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/v1/users/me`);
        if (isMounted) {
          setAgentId(data.body?.actual_id);
        }
      } catch (err) {
        console.error("Error fetching agent ID:", err);
      }
    };
    fetchId();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!agentId) return;

    let isMounted = true;
    const fetchNotifications = async () => {
      try {
        const res = await axiosInstance.get(`/api/v1/notifications/by-user/${agentId}`);
        if (isMounted) {
          setNotifications(res.data.body || []);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };
    fetchNotifications();

    const ws = new WebSocket(getWsUrl(agentId));
    wsRef.current = ws;
    ws.onopen = () => console.log("✅ Notification WebSocket connected");
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "NOTIFICATION") {
          setNotifications((prev) => [
            {
              id: data.id,
              title: data.title,
              message: data.message,
              link: data.link,
              created_at: data.created_at,
              is_read: false,
            },
            ...prev,
          ]);
        }
      } catch (err) {
        console.error("WS message error:", err);
      }
    };
    ws.onclose = () => console.log("Notification WebSocket closed");
    ws.onerror = (err) => console.error("WebSocket error:", err);

    return () => {
      isMounted = false;
      if (wsRef.current) wsRef.current.close();
    };
  }, [agentId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    try {
      await axiosInstance.patch(`/api/v1/requests/notifications/${notificationId}/read-count`);
    } catch (err) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: false } : n))
      );
      console.error("Failed to mark as read", err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await Promise.all(
      unread.map((n) =>
        axiosInstance.patch(`/api/v1/requests/notifications/${n.id}/read-count`).catch(() => { })
      )
    );
  }, [notifications]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      if (!notification.is_read) await markAsRead(notification.id);

      let rawTarget = notification.link || notification.ticket_id || notification.target_id || "";

      if (!rawTarget) {
        const text = `${notification.title || ""} ${toText(notification.message || "")}`;
        const match = text.match(/ST-[A-Za-z0-9]+/i);
        if (match) {
          rawTarget = match[0];
        }
      }

      if (rawTarget) {
        let path = String(rawTarget).trim();

        if (path.startsWith("http://") || path.startsWith("https://")) {
          try {
            path = new URL(path).pathname;
          } catch (e) { }
        }

        if (path.startsWith("/admin/support/tickets/")) {
          navigate(path);
        } else if (path.startsWith("/support-agent/tickets/")) {
          const id = path.replace("/support-agent/tickets/", "");
          navigate(`/admin/support/tickets/${id}`);
        } else if (path.startsWith("/tickets/")) {
          const id = path.replace("/tickets/", "");
          navigate(`/admin/support/tickets/${id}`);
        } else if (path.startsWith("/inbox/")) {
          const id = path.replace("/inbox/", "");
          navigate(`/admin/support/tickets/${id}`);
        } else if (/^[A-Za-z0-9_-]+$/.test(path)) {
          navigate(`/admin/support/tickets/${path}`);
        } else {
          if (!path.startsWith("/admin/support")) {
            path = `/admin/support${path.startsWith("/") ? "" : "/"}${path}`;
          }
          navigate(path);
        }
      } else {
        navigate("/admin/support/tickets");
      }

      setOpen(false);
    },
    [markAsRead, navigate]
  );

  const getRelativeTime = useCallback((ts) => {
    if (!ts) return "";
    try {
      return formatDistanceToNow(new Date(ts), { addSuffix: true });
    } catch {
      return "";
    }
  }, []);

  const toggleDropdown = useCallback(() => setOpen((v) => !v), []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
      >
        <Bell className="w-6 h-6 text-[#3B3D41]" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#3250FF] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-24px)] sm:w-[350px] bg-white rounded-[20px] p-3 border border-gray-100 shadow-[0_10px_35px_rgba(0,0,0,0.12)] z-50 overflow-hidden">
          <div className="flex items-center justify-between px-1 pb-3">
            <h3 className="text-[17px] font-bold text-[#1E293B]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-semibold text-[#603CFF] hover:text-[#4F33E0] transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`p-2 rounded-xl cursor-pointer transition-all ${!n.is_read
                      ? "bg-[#E7EBFF] hover:bg-[#E0E0FB]"
                      : "bg-white border border-gray-100 hover:bg-gray-50"
                    }`}
                >
                  <p className={`text-[13px] leading-snug font-medium ${!n.is_read ? "text-[#1E293B]" : "text-[#475569]"}`}>
                    {toText(n.message || n.title)}
                  </p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    {getRelativeTime(n.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>

          {notifications.length > 10 && (
            <div
              className="mt-3 pt-2 text-center text-xs font-semibold cursor-pointer text-[#603CFF] hover:text-[#4F33E0] transition-colors border-t border-gray-100"
              onClick={() => {
                navigate("/admin/support/notifications");
                setOpen(false);
              }}
            >
              View all {notifications.length} notifications →
            </div>
          )}
        </div>
      )}
    </div>
  );
});

NotificationBell.displayName = "NotificationBell";

/* ─── USER DROPDOWN ──────────────────────────────────────────────────────── */
const UserDropdown = memo(({ isOpen, onToggle, onLogout, navigate }) => {
  const dropdownRef = useRef(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let isMounted = true;
    axiosInstance
      .get(`/api/v1/users/me`)
      .then(({ data }) => {
        if (isMounted) {
          const u = data?.body || data || {};
          setProfile(u);
        }
      })
      .catch(() => { });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onToggle]);

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const name =
    profile?.actual_name ||
    profile?.name ||
    profile?.full_name ||
    storedUser.name ||
    storedUser.full_name ||
    "Support Agent";
  const email = profile?.email || storedUser.email || "";
  const role = profile?.role || storedUser.role || "Support Agent";
  const avatarUrl =
    profile?.avatar_url ||
    profile?.avatar ||
    profile?.profile_pic ||
    profile?.profile_image ||
    storedUser.avatar_url ||
    storedUser.avatar ||
    "";

  const initials = useMemo(() => {
    return name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("");
  }, [name]);

  const handleProfileClick = useCallback(() => {
    navigate("/admin/support/my-profile");
    onToggle(false);
  }, [navigate, onToggle]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => onToggle(!isOpen)}
        className="flex items-center px-2 py-1 gap-3 justify-between rounded-full bg-[#FFFFFFB2] border border-[#EBE7F8] hover:bg-white transition-colors"
      >
        <div className="w-9 h-9 rounded-full bg-[#3250FF] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
          {avatarUrl ? (
            <img
              src={resolveAssetUrl(avatarUrl)}
              alt={name}
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            initials || <User className="w-4 h-4" />
          )}
        </div>
        <div className="hidden sm:flex flex-col items-start leading-[1.1] min-w-0 flex-1">
          <span className="text-sm font-bold text-[#1B1730] truncate w-full text-left">
            {name}
          </span>
          <span className="text-xs text-[#6B6478] font-normal truncate w-full text-left mt-0.5">
            {role}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[#1B1730] flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl border border-gray-100 shadow-[0_10px_35px_rgba(0,0,0,0.12)] z-50 overflow-hidden">
          <div
            className="px-4 py-3 flex items-center gap-3"
            style={{ background: "#FFFFFF", borderBottom: "1px solid #EEF0F5" }}
          >
            <div className="w-9 h-9 rounded-full bg-[#3250FF] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img
                  src={resolveAssetUrl(avatarUrl)}
                  alt={name}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                initials || <User className="w-4 h-4" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {name}
              </p>
              {email && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{email}</p>
              )}
            </div>
          </div>

          <div className="py-1">
            <button
              onClick={handleProfileClick}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-800 hover:bg-[#F3F5F7] transition-colors"
            >
              <User className="w-4 h-4 text-slate-600" />
              My Profile
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

UserDropdown.displayName = "UserDropdown";

/* ─── BREADCRUMB COMPONENT (SuperAdminHeader structure) ─────────────────── */
const Breadcrumb = memo(({ navigate }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const rawPath = location.pathname;
  const basePrefix = "/admin/support";
  const relPath = rawPath.startsWith(basePrefix) ? rawPath.slice(basePrefix.length) : rawPath;

  const [, forceRender] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const onUpdate = () => forceRender();
    window.addEventListener("ticketNameUpdated", onUpdate);
    window.addEventListener("kbNameUpdated", onUpdate);
    window.addEventListener("formNameUpdated", onUpdate);
    return () => {
      window.removeEventListener("ticketNameUpdated", onUpdate);
      window.removeEventListener("kbNameUpdated", onUpdate);
      window.removeEventListener("formNameUpdated", onUpdate);
    };
  }, []);

  const breadcrumbs = useMemo(() => {
    const list = [];
    const addCrumb = (name, path = null) => list.push({ name: toText(name), path });

    if (relPath === "" || relPath === "/" || relPath === "/overview") {
      addCrumb("Overview");
    } else if (relPath.startsWith("/tickets")) {
      addCrumb("Ticket Overview", `${basePrefix}/tickets`);
      if (relPath !== "/tickets") {
        const ticketId = relPath.split("/").pop();
        if (ticketId && ticketId !== "tickets") {
          const title = getTicketName(ticketId, location, searchParams);
          addCrumb(title || `Ticket ${ticketId}`);
        }
      }
    } else if (relPath.startsWith("/inbox")) {
      addCrumb("Inbox", `${basePrefix}/inbox`);
      if (relPath !== "/inbox") {
        const ticketId = relPath.split("/").pop();
        if (ticketId && ticketId !== "inbox") {
          const title = getTicketName(ticketId, location, searchParams);
          addCrumb(title || `Ticket ${ticketId}`);
        }
      }
    } else if (relPath === "/queues") {
      addCrumb("Queue Overview");
    } else if (relPath.startsWith("/knowledge-base") || relPath.startsWith("/edit-knowledge-base")) {
      addCrumb("Knowledge Overview", `${basePrefix}/knowledge-base`);
      if (relPath === "/knowledge-base/new" || relPath.startsWith("/knowledge-base/new")) {
        addCrumb("Add Article");
      } else if (relPath.startsWith("/knowledge-base/article/")) {
        const articleId = relPath.split("/").pop();
        if (articleId) {
          const title = location.state?.title || safeStorageGet(`kb_title_${articleId}`);
          addCrumb(title || `Article ${articleId}`);
        }
      } else if (relPath.startsWith("/edit-knowledge-base/")) {
        const articleId = relPath.split("/").pop();
        if (articleId) {
          const title = location.state?.title || safeStorageGet(`kb_title_${articleId}`);
          addCrumb("Edit KB");
          addCrumb(title || `Article ${articleId}`);
        }
      }
    } else if (relPath.startsWith("/forms")) {
      addCrumb("Forms Overview", `${basePrefix}/forms`);
      if (relPath.endsWith("/forms/new")) {
        addCrumb("New Form");
      } else if (relPath !== "/forms") {
        const formId = relPath.split("/").pop();
        const title = location.state?.title || safeStorageGet(`form_name_${formId}`);
        addCrumb(title || "Edit Form");
      }
    } else if (relPath === "/my-profile") {
      addCrumb("Profile");
    } else {
      const segments = relPath.split("/").filter(Boolean);
      let cumulative = "";
      segments.forEach((segment) => {
        cumulative += `/${segment}`;
        const mapped = ROUTE_MAP[cumulative] || segment.replace(/-/g, " ");
        const label = mapped.charAt(0).toUpperCase() + mapped.slice(1);
        addCrumb(label, `${basePrefix}${cumulative}`);
      });
    }
    return list;
  }, [relPath, location, searchParams]);

  const handleHomeClick = useCallback(() => {
    navigate(`${basePrefix}/overview`);
  }, [navigate]);

  return (
    <div className="flex items-center space-x-1 text-[13px] text-gray-600 flex-wrap">
      <i
        className="pi pi-home text-[#475569] cursor-pointer hover:text-[#3250FF] transition-colors"
        onClick={handleHomeClick}
        title="Home"
      />
      {breadcrumbs.length > 0 && (
        <ChevronRight className="h-6 w-6 text-[#3250FF] flex-shrink-0" />
      )}
      {breadcrumbs.map((crumb, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <ChevronRight className="h-5 w-5 text-[#3250FF] flex-shrink-0" />
          )}
          <span
            onClick={() =>
              index < breadcrumbs.length - 1 && crumb.path && navigate(crumb.path)
            }
            className={`${index < breadcrumbs.length - 1 && crumb.path
                ? "text-[#3250FF] hover:text-[#2036CC] hover:underline cursor-pointer font-medium"
                : "text-[#0F172A] font-semibold"
              } truncate max-w-[200px] transition-colors`}
          >
            {crumb.name}
          </span>
        </Fragment>
      ))}
    </div>
  );
});

Breadcrumb.displayName = "Breadcrumb";

/* ─── MAIN HEADER COMPONENT ──────────────────────────────────────────────── */
const SupportAgentHeader = ({ onOpenSidenav }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { slot, titleSlot } = useHeaderSlot();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const path = useMemo(
    () => location.pathname.replace("/admin/support", ""),
    [location.pathname]
  );
  const isOverviewPage = useMemo(
    () => path === "" || path === "/" || path === "/overview",
    [path]
  );

  const currentHeading = useMemo(() => {
    if (path === "" || path === "/" || path === "/overview") {
      return "Dashboard";
    }
    if (path.includes("/tickets")) {
      const segments = path.split("/").filter(Boolean);
      const ticketId = segments[segments.length - 1];
      if (ticketId && ticketId !== "tickets") {
        const ticketName = getTicketName(ticketId, location, searchParams);
        return ticketName || `Ticket ${ticketId}`;
      }
      return "Tickets";
    }
    if (path.includes("/inbox")) {
      const segments = path.split("/").filter(Boolean);
      const ticketId = segments[segments.length - 1];
      if (ticketId && ticketId !== "inbox") {
        const ticketName = getTicketName(ticketId, location, searchParams);
        return ticketName || `Ticket ${ticketId}`;
      }
      return "Inbox";
    }
    if (path.includes("/queues")) {
      return "Queues";
    }
    if (path.includes("/knowledge-base")) {
      if (path === "/knowledge-base/new" || path.endsWith("/knowledge-base/new") || path.includes("/knowledge-base/new")) return "Add Article";
      const segments = path.split("/").filter(Boolean);
      if (path.includes("/knowledge-base/article/")) {
        const articleId = segments[segments.length - 1];
        if (articleId) {
          const fromState = location.state?.title;
          const fromStorage = safeStorageGet(`kb_title_${articleId}`);
          return fromState || fromStorage || "Knowledge Base Article";
        }
      }
      if (path.includes("/edit-knowledge-base/")) return "Edit Knowledge Base";
      if (path.includes("/new/")) return "Add Article";
      return "Knowledge";
    }
    if (path.includes("/forms")) {
      if (path.endsWith("/forms/new")) return "New Form";
      const segments = path.split("/").filter(Boolean);
      const last = segments[segments.length - 1];
      if (last && last !== "forms") {
        const title = location.state?.title || safeStorageGet(`form_name_${last}`);
        return title || "Edit Form";
      }
      return "Forms";
    }
    if (path === "/my-profile") {
      return "Profile";
    }
    const mapped = ROUTE_MAP[path] ||
      path.split("/").filter(Boolean).pop()?.replace(/-/g, " ") ||
      "Overview";
    return mapped
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, [path, location, searchParams]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    try { sessionStorage.clear(); } catch { }
    navigate("/auth/sign-in");
  }, [navigate]);

  const handleHomeClick = useCallback(() => {
    navigate("/admin/support/overview");
  }, [navigate]);

  const handleInboxClick = useCallback(() => {
    navigate("/admin/support/inbox");
  }, [navigate]);

  return (
    <div className="w-full">
      <div className="flex pt-5 pb-2 px-6 items-center justify-between w-full" style={{ background: 'transparent' }}>
        <div className="flex items-center min-w-0 flex-1">
          {isMobile && (
            <button
              onClick={onOpenSidenav}
              className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
          )}
          <div className="flex flex-col justify-center min-w-0">
            <div className="flex items-center gap-2">
              {!isMobile && isOverviewPage && (
                <i
                  className="pi pi-home text-gray-400 text-lg cursor-pointer transition-colors hover:text-[#3250FF]"
                  onClick={handleHomeClick}
                  title="Home"
                />
              )}
              {titleSlot ? (
                <div className="flex items-center">{titleSlot}</div>
              ) : (
                <h1 className="text-xl font-semibold text-[#0F172A] truncate capitalize max-w-[180px] sm:max-w-none">
                  {toText(currentHeading)}
                </h1>
              )}
            </div>
            {!isOverviewPage && (
              <div className="mt-0.5 hidden sm:flex">
                <Breadcrumb navigate={navigate} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3 ml-4">
          {slot && <div className="flex items-center">{slot}</div>}
          <div className="flex items-center space-x-2">
            <div className="hidden sm:block">
              <InboxButton
                active={location.pathname.includes("/inbox")}
                onClick={handleInboxClick}
              />
            </div>
            <AvailabilityControl />
            <NotificationBell />
            <UserDropdown
              isOpen={showDropdown}
              onToggle={setShowDropdown}
              onLogout={handleLogout}
              navigate={navigate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(SupportAgentHeader);
